import classNames from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import StarsButton from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import { gameDimensions } from '@swf/core/analytics/casino';
import { GADimension } from '@swf/core/enums';
import useSingleVideo from '@swf/core/hooks/useSingleVideo';
import useTrackers from '@swf/core/hooks/useTrackers';
import type { Dispatch } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';

import RoundPlayButton from '../CasinoPlaySelector/RoundPlayButton/RoundPlayButton';

import {
  TXTWCL_Play_Game,
  TXTWCL_Watch_Trailer,
  TXTWCL_Watch_again
} from './GameTrailerIcon.messages.json';
import styles from './GameTrailerIcon.scss';

export interface Props {
  className?: string;
  index?: number;
  game: CasinoGame;
  favouriteIcon?: JSX.Element;
  onLaunch?(game: CasinoGame): void;
  triggerPlay?: number;
  onPlaying?(isPlaying: boolean): void;
  autoPlay?: boolean;
}

const GameTrailerIcon: React.FC<Props> = ({
  game,
  className,
  onLaunch,
  index = 0,
  onPlaying,
  triggerPlay = 0,
  autoPlay = false
}) => {
  const vidRef = useRef<HTMLVideoElement>(null);
  const [playCount, setPlayCount] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const { casino } = useDispatch<Dispatch>();
  const [muted, setMuted] = useState(true);
  const [setPlayingVideo] = useSingleVideo();
  const { buttonPressed, trackTrailer } = useTrackers();

  const onPlay = () => {
    if (vidRef?.current?.currentTime || 0 > 10) {
      buttonPressed({ buttonName: 'Resume' }, gameDimensions(game, index));
    }
    setPlayingVideo(vidRef);
    setPlaying(true);
    onPlaying && onPlaying(true);
    ended && setEnded(false);
  };

  const onPlayGame = (game: CasinoGame) => {
    buttonPressed(
      { buttonName: 'Game Launched' },
      { [GADimension.LaunchSource]: 'Post Trailer CTA', ...gameDimensions(game, index) }
    );
    casino.launch({ game });
    onLaunch && onLaunch(game);
  };

  useEffect(() => {
    if (playCount !== triggerPlay && vidRef?.current?.paused) {
      setPlayCount(triggerPlay);
      vidRef?.current?.play();
    }
  }, [triggerPlay, playCount, playing]);

  const onPause = (game: CasinoGame) => {
    if (vidRef?.current?.currentTime === vidRef?.current?.duration) {
      trackTrailer({ game, index, action: 'Trailer Viewed' });
    } else {
      buttonPressed({ buttonName: 'Paused' }, gameDimensions(game, index));
    }
    onPlaying && onPlaying(false);
  };

  const onWatchAgain = (game: CasinoGame) => {
    buttonPressed({ buttonName: 'Watch Again' }, gameDimensions(game, index));
    vidRef?.current?.play();
  };

  const onTrailerEnd = () => {
    setEnded(true);
  };

  const onVolume = (game: CasinoGame) => {
    if (vidRef?.current?.muted !== muted) {
      buttonPressed({ buttonName: muted ? 'Unmuted' : 'Muted' }, gameDimensions(game, index));
      setMuted(!!vidRef?.current?.muted);
    }
  };

  return (
    <div className={classNames(styles.container, className)}>
      <video
        ref={vidRef}
        controls
        className={classNames(styles.icon, { [styles.videoShadow]: !playing })}
        poster={game.images.icnGameTrailerIcon || game.images.icnWide}
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback"
        onPlay={() => onPlay()}
        onPause={() => onPause(game)}
        onEnded={() => onTrailerEnd()}
        onVolumeChange={() => onVolume(game)}
        muted
        playsInline
        autoPlay={autoPlay}
      >
        <source src={game.images.icnGameTrailerVideo} type="video/mp4" />
      </video>

      {!playing && !vidRef.current?.ended && !autoPlay && (
        <StarsButton
          className={classNames(styles.action, styles.watch)}
          onClick={() => vidRef?.current?.play()}
          inverse
          variant="casino"
          translation={{ ...TXTWCL_Watch_Trailer }}
          icon={<span className={styles.arrowRight} />}
        />
      )}

      {ended && (
        <div className={styles.endScreen}>
          <div className={styles.endContainer}>
            <div
              className={classNames(styles.endButtons, styles.topGroup)}
              onClick={() => onWatchAgain(game)}
            >
              <div className={classNames(styles.replay)} />
              <span>
                <Translation {...TXTWCL_Watch_again} />
              </span>
            </div>
            <div
              className={classNames(styles.endButtons, styles.bottomGroup)}
              onClick={() => onPlayGame(game)}
            >
              <RoundPlayButton className={classNames(styles.playButton)} title={game.name} />
              <span>
                <Translation {...TXTWCL_Play_Game} tagName="span" />
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameTrailerIcon;
